'use client';

import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import type { shopDataLessProps, shopDataProps } from '../../data/shopdata.type';

import { AuthContext } from '@/components/common/AuthProvider/AuthProvider';
import { CartContext } from '@/app/(ordering)/shop/CartProvider/CartProvider';
import DateTimeComponent from '@/components/ordering/DateTimeComponent/DateTimeComponent';
import { PickupAddressProps } from '@/app/(ordering)/shop/CartProvider/cartProviderTypes';
import PickupSelection from './PickupSelection';
import ShopSearch from './ShopSearch';
import { getShopByID } from '../../data/shopdata';
import { trackJourneyElement } from '@/utils/eventTracking';

export type PickupCurrentView = 'dateTime' | 'searching' | 'selection';

type PickupPanelProps = {
	closeModal: Dispatch<SetStateAction<void>>;
	isModalOpen: boolean;
	redirectedFrom?: string;
};
export function PickupPanel({ closeModal, isModalOpen, redirectedFrom }: PickupPanelProps) {
	const [currentView, setCurrentView] = useState<PickupCurrentView>('selection');
	const { userDetails } = useContext(AuthContext);
	const { address, setNewAddress, apiCart } = useContext(CartContext);
	const [selectedShop, setSelectedShop] = useState<shopDataLessProps | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const setShopUtil = async (shopId: string): Promise<shopDataProps | null> => {
		setIsLoading(true);
		try {
			const shop = await getShopByID({ shopId });
			setSelectedShop(shop);
			return shop;
		} catch (error) {
			console.error('Error fetching shop:', error);
			setIsLoading(false);
			return null;
		} finally {
			setIsLoading(false);
		}
	};

	const setShop = async () => {
		try {
			if (apiCart) {
				await setShopUtil(apiCart.storeNumber.toString());
			} else if (userDetails?.favoriteShopId) {
				// If basket does not exist, create one with the user's favorite shop
				const shop = await setShopUtil(userDetails.favoriteShopId.toString());

				// Ensure shop is not null before using it
				if (!shop) {
					console.warn('Shop is unavailable or could not be fetched.');
					return;
				}

				// Create basket here
				const shopAdd: PickupAddressProps = {
					shopId: shop.shopId,
					shopName: shop.shopName,
					streetAddress: shop.address1,
					city: shop.city,
					zipCode: shop.zipCode,
					latitude: shop.latitude,
					longitude: shop.longitude,
				};

				await setNewAddress({
					type: 'pickup',
					deliveryAddress: null,
					pickupAddress: shopAdd,
					orderDateTime: address.orderDateTime,
				});
			}
		} catch (error) {
			console.error('Error in getting shop: ', error);
		}
	};

	useEffect(() => {
		// If shop is selected and that shop is favourite shop which is unavailable then do not set shops and menu data
		if (isModalOpen && apiCart?.restaurant?.isAvailable) {
			setShop();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apiCart, userDetails, isModalOpen]);

	useEffect(() => {
		if (redirectedFrom === 'review') {
			setCurrentView('searching');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleLocationSelect = (shop: shopDataProps) => {
		trackJourneyElement({
			type: 'location',
			action: 'select',
			location: 'pickup search',
			pathname: window.location.pathname,
			domain: window.location.hostname,
		});
		setSelectedShop(shop);
		setCurrentView('selection');
	};

	return (
		<>
			{currentView === 'searching' && (
				<ShopSearch
					onShopSelected={(shop: shopDataProps) => {
						handleLocationSelect(shop);
					}}
					setCurrentView={setCurrentView}
				/>
			)}
			{currentView === 'dateTime' && (
				<DateTimeComponent orderType="Pickup" onCloseDateTime={() => setCurrentView('selection')} />
			)}

			{currentView === 'selection' && (
				<PickupSelection
					closeModal={closeModal}
					selectedShop={selectedShop}
					setCurrentView={setCurrentView}
					isLoadingPickupPanel={isLoading}
				/>
			)}
		</>
	);
}
